import React, { useRef, useEffect, useState } from 'react';

/**
 * StepTimerPopover - Discrete popover showing timer information for a step
 * Appears when step is clicked, positioned near the step
 */
const StepTimerPopover = ({ timingSegments = [], stepText, stepNumber, onClose }) => {
    const popoverRef = useRef(null);
    const [position, setPosition] = useState({ top: '100%', left: '0', transform: 'none' });

    // Parse timing segments if they're a string
    let segments = timingSegments;
    if (typeof timingSegments === 'string') {
        try {
            segments = JSON.parse(timingSegments);
        } catch (e) {
            segments = [];
        }
    }

    // Ensure segments is always an array
    if (!Array.isArray(segments)) {
        segments = [];
    }

    // Find active and inactive segments
    const activeSegment = segments.find(s => s.kind === 'active') || {
        kind: 'active',
        label: 'Active time',
        expectedSeconds: 0
    };
    const inactiveSegment = segments.find(s => s.kind === 'inactive') || {
        kind: 'inactive',
        label: 'Inactive time',
        expectedSeconds: 0
    };

    const formatTime = (seconds) => {
        if (seconds === 0) return '0m';
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (minutes === 0) return `${secs}s`;
        if (secs === 0) return `${minutes}m`;
        return `${minutes}m ${secs}s`;
    };

    // Position the popover to avoid viewport overflow
    useEffect(() => {
        if (popoverRef.current) {
            const rect = popoverRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            let newPosition = { top: '100%', left: '0', transform: 'none', marginTop: '8px' };
            
            // Check if popup overflows right edge
            if (rect.right > viewportWidth - 20) {
                newPosition.left = 'auto';
                newPosition.right = '0';
            }
            
            // Check if popup overflows bottom edge
            if (rect.bottom > viewportHeight - 20) {
                newPosition.top = 'auto';
                newPosition.bottom = '100%';
                newPosition.marginTop = '0';
                newPosition.marginBottom = '8px';
            }
            
            setPosition(newPosition);
        }
    }, []);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };

        // Close on escape key
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        // Use setTimeout to avoid the click that opened the popover from closing it
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }, 10);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // Don't show if no timers
    if (activeSegment.expectedSeconds === 0 && inactiveSegment.expectedSeconds === 0) {
        return null;
    }

    return (
        <div
            ref={popoverRef}
            className="absolute z-[9999] border border-neutral-200 shadow-xl p-3 min-w-[200px]"
            style={{ 
                ...position,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
        >
            <div 
                className="font-mono text-xs uppercase tracking-wider mb-2"
                style={{ color: '#0D0D0D', opacity: 0.6 }}
            >
                TIMING
            </div>
            
            <div className="space-y-2">
                {activeSegment.expectedSeconds > 0 && (
                    <div className="flex items-center justify-between gap-4">
                        <span 
                            className="text-xs"
                            style={{ color: '#0D0D0D', opacity: 0.8 }}
                        >
                            {activeSegment.label || 'Active time'}
                        </span>
                        <span 
                            className="font-mono text-xs font-medium"
                            style={{ color: '#0D0D0D' }}
                        >
                            {formatTime(activeSegment.expectedSeconds)}
                        </span>
                    </div>
                )}
                
                {inactiveSegment.expectedSeconds > 0 && (
                    <div className="flex items-center justify-between gap-4">
                        <span 
                            className="text-xs"
                            style={{ color: '#0D0D0D', opacity: 0.8 }}
                        >
                            {inactiveSegment.label || 'Inactive time'}
                        </span>
                        <span 
                            className="font-mono text-xs font-medium"
                            style={{ color: '#0D0D0D' }}
                        >
                            {formatTime(inactiveSegment.expectedSeconds)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StepTimerPopover;
